import { styled, Typography, type TypographyProps } from "@mui/material";
import { styledOmitPropsOptions } from "../theme/styled.helpers";
import { typographyComponents } from "../theme/typographyComponents";
import { isNil } from "lodash";

const typographyComponentsObject = typographyComponents as Record<string, any>;

type EnhancedTypographyBaseProps = {
    variant?: string;
    strong?: boolean;
    scaleDown?: boolean;
    light?: boolean;
    withoutScale?: boolean;
};
type CustomVariantProps = Omit<TypographyProps, "variant"> & EnhancedTypographyBaseProps;

// WithoutScale: Map custom variant names to MUI typography keys
const WithoutScaleTypographyVariantMap: Record<string, keyof typeof typographyComponents> = {
    h1: "headingXL",
    h2: "headingL",
    h3: "headingM",
    h4: "headingS",
};

// ScaleDown: Map custom variant names to MUI typography keys
const ScaleDownTypographyVariantMap: Record<string, keyof typeof typographyComponents> = {
    h1: "headingM",
    h2: "headingS",
    h3: "headingXS",
    bodyTextM: "bodyTextS",
};

const EnhancedTypographyBase = styled(
    Typography,
    styledOmitPropsOptions,
)<EnhancedTypographyBaseProps>(({ theme, variant, strong, scaleDown, light, withoutScale }) =>
    theme.unstable_sx({
        // main spread of styles based on variant
        //	...(variant && typographyComponents[variant as keyof typeof typographyComponents]),
        // above is better but not working when typed TypographyOptions correctly
        ...(!isNil(variant) && typographyComponentsObject[variant]),

        // strong
        ...(strong && {
            fontWeight: "bold",
        }),

        // light
        ...(light && {
            color: theme.palette.grey[600],
        }),

        // withoutScale
        ...(!withoutScale &&
            ["h1", "h2", "h3", "h4"].includes(variant ?? "") && {
                [theme.breakpoints.up("md")]: {
                    fontSize: (theme.typography as any)[WithoutScaleTypographyVariantMap[variant ?? ""]]
                        ? (theme.typography as any)[WithoutScaleTypographyVariantMap[variant ?? ""]].fontSize
                        : undefined,
                },
            }),

        // scaleDown
        ...(scaleDown &&
            ["h1", "h2", "h3", "bodyTextM"].includes(variant ?? "") && {
                [theme.breakpoints.up("md")]: {
                    fontSize: (theme.typography as any)[ScaleDownTypographyVariantMap[variant ?? ""]]
                        ? (theme.typography as any)[ScaleDownTypographyVariantMap[variant ?? ""]].fontSize
                        : undefined,
                },
            }),
    }),
);

export const HeadingXL = styled(
    (props: CustomVariantProps) => <EnhancedTypographyBase component="h1" {...props} variant="h1" />,
    styledOmitPropsOptions,
)(({ theme }) => ({
    // Use the theme's typography
}));

export const HeadingL = styled(
    (props: CustomVariantProps) => <EnhancedTypographyBase component="h1" {...props} variant="h1" />,
    styledOmitPropsOptions,
)(({ theme }) => ({
    // Use the theme's typography
}));

export const HeadingM = styled(
    (props: CustomVariantProps) => <EnhancedTypographyBase component="h2" {...props} variant="h2" />,
    styledOmitPropsOptions,
)(({ theme }) => ({
    // Use the theme's typography
}));

export const HeadingS = styled(
    (props: CustomVariantProps) => <EnhancedTypographyBase component="h3" {...props} variant="h3" />,
    styledOmitPropsOptions,
)(({ theme }) => ({
    // Use the theme's typography
}));

export const HeadingXS = styled(
    (props: CustomVariantProps) => <EnhancedTypographyBase component="h4" {...props} variant="h4" />,
    styledOmitPropsOptions,
)(({ theme }) => ({
    // Use the theme's typography
}));

export const BodyTextXS = styled(
    (props: CustomVariantProps) => <EnhancedTypographyBase component="p" {...props} variant="bodyTextXS" />,
    styledOmitPropsOptions,
)(({ theme }) => ({
    // Use the theme's typography
}));

export const BodyTextS = styled(
    (props: CustomVariantProps) => <EnhancedTypographyBase component="p" {...props} variant="bodyTextS" />,
    styledOmitPropsOptions,
)(({ theme }) => ({
    // Use the theme's typography
}));

export const BodyTextM = styled(
    (props: CustomVariantProps) => <EnhancedTypographyBase component="p" {...props} variant="bodyTextM" />,
    styledOmitPropsOptions,
)(({ theme }) => ({
    // Use the theme's typography
}));

export const BodyTextL = styled(
    (props: CustomVariantProps) => <EnhancedTypographyBase component="p" {...props} variant="bodyTextL" />,
    styledOmitPropsOptions,
)(({ theme }) => ({
    // Use the theme's typography
}));

export const EnhancedTypography = {
    HeadingXL,
    HeadingL,
    HeadingM,
    HeadingS,
    HeadingXS,
    BodyTextXS,
    BodyTextS,
    BodyTextM,
    BodyTextL,
};
